// Open Haunt (/haunt), the first authored zone (zones/open-haunt/zone.json, docs/build/WAVE12-WORLDS.md): a
// full-bleed haunted hero (a poster first, then the live 3D scene built from the Halloween pack), the season's clock,
// the pumpkin quest, the Halloween row as a showcase, the pack, Haunt night in the Plaza and, last, the creators'
// way in. Copy from docs/launch/open-haunt.md where it has the words; no CLI commands on this page (they live in the
// guide and on /developers).

import { api } from "../../api.ts";
import { getCatalogue, refreshCatalogue, type Game } from "../../catalogue.ts";
import { coverStyle, lazyCover, playButton } from "../../components.ts";
import { h, link } from "../../dom.ts";
import { loadCalendar, type PlazaEvent } from "../../events.ts";
import { halloweenNight, minutesOf, nightTime, progressText, questFrom, type QuestProgress } from "../../haunt.ts";
import { GUIDE_GATE, HAUNT_FREE, HAUNT_HERO, HAUNT_HOW, HAUNT_OPEN, HAUNT_PUMPKIN, HAUNT_RULES } from "./copy.ts";
import { posterCard } from "../../pages/home.ts";
import { ctxWith, line, passes, type Ctx } from "../../pages/developers.ts";
import { navigate, setTitle } from "../../router.ts";
import { HALLOWEEN_ROW } from "../../rows.ts";
import { signedIn } from "../../session.ts";
import { mark, mountHero } from "./hero.ts";
import { assetHref, HERO_POSTER, HERO_POSTER_TALL, NIGHT_ART, PACK, previewSrc, TEMPLATE_ART } from "./pack.ts";
import { countdownText, heroClock, NIGHT_AT, NIGHT_UNTIL, nightClock, phaseAt, timeline, zoneNow } from "./season.ts";
import { ensureZoneStyle } from "./style.ts";
import { pumpkinIcon } from "./svg.ts";

/** Everything the zone's modules read, fetched once per visit (cached for a few seconds, so the zone shell's separate
 * sections share one set of requests). */
export interface HauntData {
  quest: QuestProgress | null;
  night: PlazaEvent | null;
  c: Ctx;
  boosted: Set<string>;
  /** the Halloween row's games (empty before accounts opens the season) */
  games: Game[];
  /** the row is there: accounts' clock says the season is on */
  live: boolean;
  /** what the showcase shows: the row, or spooky warm-up picks before it */
  shown: Game[];
}

let cached: { at: number; data: Promise<HauntData> } | null = null;

export function loadHauntData(): Promise<HauntData> {
  if (cached && Date.now() - cached.at < 5000) return cached.data;
  const data = (async () => {
    const [cat, quest, cal] = await Promise.all([
      getCatalogue().catch(() => null),
      signedIn() ? api<unknown>("accounts", "/me/unlocks").then(questFrom, () => null) : Promise.resolve(null),
      loadCalendar().catch(() => null),
    ]);
    const night = cal ? halloweenNight(cal) : null;
    const c = ctxWith({
      "world: plaza night time": night ? nightTime(night) : undefined,
      ...(minutesOf(quest) ? { "rows: minutes": minutesOf(quest) } : {}),
    });
    const row = cat?.rows?.find((r) => r.id === HALLOWEEN_ROW);
    const games = row && cat ? row.games.map((id) => cat.games.find((g) => g.id === id)).filter((g): g is Game => !!g) : [];
    // accounts' clock decides the season (the row is there or not); before it, spooky games to warm up with
    const live = games.length > 0;
    return { quest, night, c, boosted: new Set(row?.boosted ?? []), games, live, shown: live ? games : cat ? warmUp(cat.games) : [] };
  })();
  cached = { at: Date.now(), data };
  return data;
}

/** The standalone /haunt page (until the zone shell takes the route: index.ts registers the same modules there). */
export async function openHauntPage(): Promise<HTMLElement> {
  setTitle("Open Haunt");
  ensureZoneStyle();
  const now = zoneNow();
  const d = await loadHauntData();
  const ticks: ((t: number) => void)[] = [];

  const root = h(
    "div",
    { class: "oh", "data-zone": "open-haunt" },
    hero(now, ticks, d.live),
    seasonStrip(now),
    questCard(d.quest, d.games, d.c),
    showcase(d.shown, d.boosted, d.live, now),
    packSection(d.c),
    nightBand(d.night, now, ticks),
    creators(d.live ? d.games : [], d.c),
  );
  startClock(root, now, ticks);
  // the zone framework's budget marks (docs/build/zones.md): first content now, the hero when its poster has loaded
  requestAnimationFrame(() => mark("zone:first-content"));
  return root;
}

// ---- hero ------------------------------------------------------------------------------------------------------------

/** The season's clock (status, what it counts down to) and the two calls to action. */
export function clockBar(now: number, ticks: ((t: number) => void)[], live: boolean): { clock: HTMLElement; actions: HTMLElement } {
  const k0 = heroClock(now);
  const digits = h("span", { class: "oh-digits", "aria-hidden": "true" }, k0.target ? countdownText(k0.target, now) : "");
  const sr = h("span", { class: "oh-sr" }, k0.label && k0.target ? `${k0.label} ${countdownText(k0.target, now)}` : "");
  const status = h("span", { class: `oh-status${k0.live ? " live" : ""}` }, k0.status);
  const label = h("span", { class: "oh-clock-label" }, k0.label ?? "");
  let phase = phaseAt(now);
  ticks.push((t) => {
    const k = heroClock(t);
    status.textContent = k.status;
    status.classList.toggle("live", k.live);
    label.textContent = k.label ?? "";
    digits.textContent = k.target ? countdownText(k.target, t) : "";
    sr.textContent = k.label && k.target ? `${k.label} ${countdownText(k.target, t)}` : "";
    const p = phaseAt(t);
    if (p !== phase) {
      phase = p;
      // a new phase (the season opening, the night): fetch the catalogue again and redraw, so the row shows up without
      // a reload. Not with the dev clock (?hauntNow), which would start over from the same moment.
      if (!new URLSearchParams(location.search).has("hauntNow")) {
        refreshCatalogue();
        cached = null;
        navigate(location.pathname + location.search + location.hash, true);
      }
    }
  });
  return {
    clock: h("div", { class: "oh-clock" }, status, label, digits, sr),
    actions: h(
      "div",
      { class: "oh-actions" },
      h("a", { class: "btn oh-btn", href: live ? "#row" : "#pumpkin" }, live ? "Play the haunts" : "Light up your pumpkin"),
      h("a", { class: "btn ghost", href: "#night" }, "Haunt night, 7 Nov"),
    ),
  };
}

function hero(now: number, ticks: ((t: number) => void)[], live: boolean): HTMLElement {
  const { clock, actions } = clockBar(now, ticks, live);
  const stage = h(
    "div",
    { class: "oh-stage", "aria-hidden": "true" },
    h("picture", null, h("source", { media: "(max-width: 760px)", srcset: HERO_POSTER_TALL }), h("img", { class: "oh-poster", src: HERO_POSTER, alt: "", decoding: "async", fetchpriority: "high", onload: () => mark("zone:hero") })),
  );
  const el = h(
    "section",
    { class: "oh-hero oh-bleed", "aria-labelledby": "oh-title" },
    stage,
    h("div", { class: "oh-fog", "aria-hidden": "true" }, h("i"), h("i"), h("i")),
    h(
      "div",
      { class: "oh-hero-copy" },
      h("p", { class: "oh-kicker" }, "Halloween on Banger", h("span", null, "23 Oct – 10 Nov")),
      h("h1", { id: "oh-title" }, HAUNT_HERO.title),
      h("p", { class: "oh-lead" }, "Spooky games from creators everywhere, free to play. Visit three haunts and light up your pumpkin."),
      clock,
      actions,
    ),
    h("a", { class: "oh-make-link", href: "#make" }, "Making a haunt? Start here"),
  );
  // the live scene replaces the poster once it has drawn a frame; the poster stays on reduced motion and low power
  queueMicrotask(() => mountHero(stage));
  return el;
}

/** The season at a glance: four moments, the next one marked. */
export function seasonStrip(now: number): HTMLElement {
  return h(
    "section",
    { class: "oh-season", "aria-label": "Open Haunt dates" },
    h(
      "ol",
      null,
      timeline(now).map(({ m, state }) =>
        h(
          "li",
          { class: `oh-moment ${state}`, ...(state === "next" || state === "now" ? { "aria-current": "step" } : {}) },
          h("span", { class: "oh-dot", "aria-hidden": "true" }),
          h("span", { class: "oh-day" }, m.day),
          m.id === "plaza-night" ? h("a", { href: "#night" }, m.label) : h("span", null, m.label),
          state === "now" ? h("span", { class: "oh-tag" }, "Now") : state === "next" ? h("span", { class: "oh-tag" }, "Next") : null,
        ),
      ),
    ),
  );
}

// ---- quest -------------------------------------------------------------------------------------------------------------

export function questCard(q: QuestProgress | null, games: Game[], c: Ctx): HTMLElement {
  const lit = q ? (q.done ? q.needed : Math.min(q.counted.length, q.needed)) : 0;
  const needed = q?.needed ?? 3;
  const titleOf = (id: string) => games.find((g) => g.id === id)?.title ?? "A haunt";
  return h(
    "section",
    { class: `oh-quest${q?.done ? " done" : ""}`, id: "pumpkin", "aria-labelledby": "oh-quest-title" },
    h(
      "div",
      { class: "oh-quest-copy" },
      h("p", { class: "oh-eyebrow" }, "Quest"),
      h("h2", { id: "oh-quest-title" }, HAUNT_PUMPKIN.heading),
      line("p", HAUNT_PUMPKIN.paras![0]!, c),
      !signedIn()
        ? h("p", { class: "oh-quest-cta" }, link(`/signin?next=${encodeURIComponent("/haunt#pumpkin")}`, { class: "btn oh-btn" }, "Sign in to track it"))
        : q
          ? h("p", { class: "oh-quest-status", "aria-live": "polite" }, progressText(q))
          : h("p", { class: "oh-quest-status dim" }, "Your progress shows here once the haunts start counting."),
      q?.counted.length ? h("ul", { class: "oh-counted" }, q.counted.slice(0, q.needed).map((id) => h("li", null, link(`/g/${id}`, null, titleOf(id))))) : null,
    ),
    h(
      "div",
      { class: "oh-quest-art" },
      h(
        "ol",
        { class: "oh-pumpkins", role: "img", "aria-label": `${lit} of ${needed} pumpkins lit` },
        Array.from({ length: needed }, (_, i) => h("li", { class: i < lit ? "lit" : "" }, pumpkinIcon(i < lit), h("span", null, `Haunt ${i + 1}`))),
      ),
      link(
        assetHref("threedom/halloween-pumpkin-head-lit"),
        { class: "oh-reward" },
        h("img", { src: previewSrc("threedom/halloween-pumpkin-head-lit"), alt: "", loading: "lazy", decoding: "async" }),
        h("span", null, h("small", null, "Reward"), "Candlelit Pumpkin Head"),
      ),
    ),
  );
}

// ---- the Halloween row --------------------------------------------------------------------------------------------------

/** Before the row opens: listed spooky games, the ones with art first. */
export function warmUp(all: Game[], max = 13): Game[] {
  const spooky = all.filter((g) => g.themes?.includes("spooky") && g.status !== "coming_soon");
  return [...spooky.filter((g) => g.art), ...spooky.filter((g) => !g.art)].slice(0, max);
}

export function showcase(games: Game[], boosted: Set<string>, live: boolean, now: number): HTMLElement {
  const head = h(
    "div",
    { class: "oh-head" },
    live
      ? h("div", null, h("p", { class: "oh-eyebrow" }, "The Halloween row"), h("h2", null, "Haunts to play tonight"))
      : h("div", null, h("p", { class: "oh-eyebrow" }, phaseAt(now) === "soon" ? "The Halloween row opens on 23 October" : "Before the row fills up"), h("h2", null, "Spooky games to warm up")),
    live ? link(`/browse?row=${HALLOWEEN_ROW}`, { class: "oh-more" }, games.length > 13 ? `See all ${games.length}` : "Browse the row") : null,
  );
  if (!games.length) {
    return h(
      "section",
      { class: "oh-showcase empty", id: "row" },
      head,
      h(
        "div",
        { class: "oh-soon" },
        h("p", null, phaseAt(now) === "soon" ? "The Halloween row opens on 23 October." : "New haunts land here as they pass review."),
        h("p", { class: "dim" }, "Players order it with their hearts and playtime."),
      ),
    );
  }
  const [lead, ...rest] = games;
  return h(
    "section",
    { class: "oh-showcase", id: "row" },
    head,
    h(
      "article",
      { class: "oh-feature", style: coverStyle(lead!, "full") },
      h(
        "div",
        { class: "oh-feature-copy" },
        h("p", { class: "oh-eyebrow" }, live ? "Featured haunt" : "Tonight\u2019s pick"),
        h("h3", null, link(`/g/${lead!.id}`, null, lead!.title)),
        h("p", { class: "oh-by" }, `by ${lead!.creator.name}`),
        lead!.blurb ? h("p", { class: "oh-blurb" }, lead!.blurb) : null,
        h("div", { class: "oh-actions" }, playButton(lead!, "small"), link(`/g/${lead!.id}`, { class: "btn ghost small" }, "Details")),
      ),
    ),
    rest.length ? h("ul", { class: "poster-track oh-track", role: "list" }, rest.slice(0, 12).map((g) => posterCard(g, boosted))) : null,
  );
}

// ---- the pack ---------------------------------------------------------------------------------------------------------

export function packSection(c: Ctx): HTMLElement {
  const props = PACK.filter((p) => !p.wear);
  const worn = PACK.filter((p) => p.wear);
  const tile = (p: (typeof PACK)[number]) =>
    h(
      "li",
      null,
      link(
        assetHref(p.id),
        { class: "oh-asset" },
        h("span", { class: `oh-asset-art${/fog|bats/.test(p.id) ? " moonlit" : ""}` }, h("img", { src: previewSrc(p.id), alt: "", loading: "lazy", decoding: "async" })),
        h("strong", null, p.title),
        h("span", { class: "dim" }, p.line),
      ),
    );
  return h(
    "section",
    { class: "oh-pack", id: "pack", "aria-labelledby": "oh-pack-title" },
    h(
      "div",
      { class: "oh-head" },
      h("div", null, h("p", { class: "oh-eyebrow" }, "Free · CC0"), h("h2", { id: "oh-pack-title" }, "The Halloween pack")),
      link("/store?collection=halloween", { class: "oh-more" }, "Get the Halloween pack"),
    ),
    h("p", { class: "oh-sub" }, "Every piece is a generator: carve a pumpkin, pick a gravestone, grow a tree, stack a haunted house. The scene at the top of this page is built from it."),
    h("ul", { class: "oh-assets" }, props.map(tile)),
    h("h3", { class: "oh-sub-head" }, "For your avatar"),
    h("ul", { class: "oh-assets worn" }, worn.map(tile)),
  );
}

// ---- Haunt night -------------------------------------------------------------------------------------------------------

export function nightBand(e: PlazaEvent | null, now: number, ticks: ((t: number) => void)[]): HTMLElement {
  const at = e?.at ?? NIGHT_AT;
  const until = e ? Math.max(at + 1, NIGHT_UNTIL - NIGHT_AT + at) : NIGHT_UNTIL;
  const k = nightClock(now, at, until);
  const clock = h("p", { class: `oh-night-clock ${k.state}` }, k.text);
  ticks.push((t) => {
    const n = nightClock(t, at, until);
    clock.textContent = n.text;
    clock.className = `oh-night-clock ${n.state}`;
  });
  const local = nightTime({ at });
  const utc = nightTime({ at }, "UTC");
  return h(
    "section",
    { class: "oh-night oh-bleed", id: "night", "aria-labelledby": "oh-night-title", style: { backgroundImage: `url("${NIGHT_ART}")` } },
    h(
      "div",
      { class: "oh-night-copy" },
      h("p", { class: "oh-eyebrow" }, "In the Plaza"),
      h("h2", { id: "oh-night-title" }, e?.title ?? "Haunt night in the Plaza"),
      h("p", { class: "oh-night-when" }, local, local === utc ? null : h("span", null, ` (${utc})`)),
      clock,
      h("p", null, "We play the week's haunts live on the stage in the Plaza. Wear your pumpkin."),
      h("div", { class: "oh-actions" }, link("/events", { class: "btn oh-btn" }, "See it in Events")),
    ),
  );
}

// ---- creators -----------------------------------------------------------------------------------------------------------

export function creators(games: Game[], c: Ctx): HTMLElement {
  const seen = new Map<string, Game>();
  for (const g of games) if (!seen.has(g.creator.id) && !g.firstParty) seen.set(g.creator.id, g);
  const spots = [...seen.values()].slice(0, 6);
  const steps = HAUNT_HOW.steps!.map((s) => line("li", s, c)).filter(Boolean);
  const guide = passes(GUIDE_GATE, c);
  return h(
    "section",
    { class: "oh-creators", id: "make", "aria-labelledby": "oh-make-title" },
    spots.length
      ? h(
          "div",
          { class: "oh-spotlights" },
          h("div", { class: "oh-head" }, h("div", null, h("p", { class: "oh-eyebrow" }, "Creator spotlights"), h("h2", null, "Who's haunting this year"))),
          h(
            "ul",
            { class: "oh-spots" },
            spots.map((g) =>
              h(
                "li",
                null,
                link(`/c/${g.creator.id}`, { class: "oh-spot" }, h("span", { class: "oh-spot-art", ...lazyCover(g) }), h("strong", null, g.creator.name), h("span", { class: "dim" }, g.title)),
              ),
            ),
          ),
        )
      : null,
    h(
      "div",
      { class: "oh-make" },
      h(
        "div",
        { class: "oh-make-copy" },
        h("p", { class: "oh-eyebrow" }, "For creators"),
        h("h2", { id: "oh-make-title" }, "Make a haunt"),
        line("p", HAUNT_HERO.lead, c, { class: "oh-lead-sm" }),
        line("p", HAUNT_HERO.dates, c, { class: "oh-dates" }),
        steps.length ? h("ol", { class: "oh-steps" }, steps) : null,
        h(
          "div",
          { class: "oh-actions" },
          guide ? link("/haunt/guide", { class: "btn oh-btn" }, "Start with The Haunt template") : link("/developers", { class: "btn oh-btn" }, "Ship on Banger"),
          link("/store?collection=halloween", { class: "btn ghost" }, "Get the Halloween pack"),
        ),
      ),
      h(
        "figure",
        { class: "oh-template" },
        h("img", { src: TEMPLATE_ART, alt: "The Haunt template: a graveyard gate in fog, a haunted house behind it", loading: "lazy", decoding: "async" }),
        h("figcaption", null, h("strong", null, "The Haunt template"), " Walk through a graveyard into a haunted house. Three scares, all sound synthesized. Free."),
      ),
    ),
    h("div", { class: "oh-notes" }, ...[HAUNT_FREE, HAUNT_OPEN].map((b) => h("section", null, h("h3", null, b.heading!), (b.paras ?? []).map((p) => line("p", p, c))))),
    h(
      "details",
      { class: "oh-rules", id: "rules" },
      h("summary", null, "Rules"),
      h(
        "div",
        { class: "rules-grid" },
        HAUNT_RULES.map((r) => {
          const items = r.list.map((t) => line("li", t, c)).filter((x) => x && x.textContent?.trim());
          return items.length ? h("section", null, h("h3", null, r.heading), h("ul", null, items)) : null;
        }),
      ),
    ),
  );
}

// ---- clock ---------------------------------------------------------------------------------------------------------------

/** One timer for every countdown on the page; stops when the page leaves the document. */
export function startClock(root: HTMLElement, start: number, ticks: ((t: number) => void)[]): void {
  const offset = start - Date.now();
  const id = window.setInterval(() => {
    if (!root.isConnected) {
      // routed away (or not mounted yet: give it a moment before deciding)
      if (root.dataset.mounted) return window.clearInterval(id);
      return;
    }
    root.dataset.mounted = "1";
    const t = Date.now() + offset;
    for (const f of ticks) f(t);
  }, 1000);
}
